import * as React from 'react';
import { useRender } from '@base-ui/react/use-render';
import { cn } from '../../lib/utils';
import { useHorizontalOverflow } from '../../hooks/use-horizontal-overflow';

/**
 * Page layout primitives — the hairline grammar that replaces card stacks.
 *
 *   PageContainer  the page frame every route sits in: token gutters and an
 *               optional max width
 *   PageHeader  title row + optional description, meta (scope/provenance
 *               chips) and actions; a sub-navigation slot sits under it
 *   SubNav      route-level sub-navigation as an underlined link row (not a
 *               segmented control, not buttons)
 *   Section     a titled region separated from its neighbours by a single
 *               hairline; tables and lists sit flush inside it
 *
 * Nothing here draws a box, a shadow or a rounded container. Hierarchy comes
 * from type weight, spacing and one-pixel rules (`.impeccable.md`).
 */

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Scope and provenance chips, counts, last-updated stamps. */
  meta?: React.ReactNode;
  /** Page-level actions; at most one primary. */
  actions?: React.ReactNode;
  /** Usually a `SubNav`; rendered flush under the title row. */
  navigation?: React.ReactNode;
  /** Small trail above the title (e.g. a back link). */
  breadcrumb?: React.ReactNode;
  headingLevel?: 1 | 2;
  /** `compact` is the phone-first header (commissioning); same type scale family, one step down. */
  size?: 'default' | 'compact';
}

const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(
  (
    {
      title,
      description,
      meta,
      actions,
      navigation,
      breadcrumb,
      headingLevel = 1,
      size = 'default',
      className,
      ...props
    },
    ref,
  ) => {
    const Heading = headingLevel === 1 ? 'h1' : 'h2';
    const compact = size === 'compact';
    return (
      <header
        ref={ref}
        data-slot="page-header"
        data-size={size}
        className={cn(
          'flex flex-col border-b border-[var(--color-hairline)]',
          compact ? 'gap-2' : 'gap-3',
          navigation ? 'pb-0' : compact ? 'pb-3' : 'pb-4',
          className,
        )}
        {...props}
      >
        {breadcrumb ? (
          <div className="text-xs text-[var(--color-foreground-3)]">{breadcrumb}</div>
        ) : null}
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0 max-w-[72ch] space-y-1">
            <Heading
              className={cn(
                'text-balance font-semibold tracking-[-0.015em] text-[var(--color-foreground)]',
                compact ? 'text-lg' : 'text-xl',
              )}
            >
              {title}
            </Heading>
            {description ? (
              <p className="text-pretty text-sm text-[var(--color-foreground-2)]">{description}</p>
            ) : null}
            {meta ? (
              <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-[var(--color-foreground-3)]">
                {meta}
              </div>
            ) : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
        {navigation}
      </header>
    );
  },
);
PageHeader.displayName = 'PageHeader';

export interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** `full` fills the shell; `wide` and `narrow` centre a capped column. */
  width?: 'full' | 'wide' | 'narrow';
}

/**
 * The page frame: token gutters on both sides and a vertical stack, so no page
 * sits flush against the sidebar. Wrap each route's content (PageHeader first)
 * in one of these.
 */
const PageContainer = React.forwardRef<HTMLDivElement, PageContainerProps>(
  ({ width = 'full', className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="page-container"
      className={cn(
        'flex w-full min-w-0 flex-col gap-5 px-4 py-5 lg:px-6',
        width === 'wide' && 'mx-auto max-w-7xl',
        width === 'narrow' && 'mx-auto max-w-3xl',
        className,
      )}
      {...props}
    />
  ),
);
PageContainer.displayName = 'PageContainer';

export interface SubNavProps extends React.HTMLAttributes<HTMLElement> {
  /** Accessible name, e.g. "Fleet sections". */
  'aria-label': string;
}

/**
 * On a narrow screen the link row scrolls sideways. While it overflows, the
 * edge that hides more links fades out (so the row reads as scrollable rather
 * than clipped), and the current page's link is scrolled into view on mount
 * so a deep section is never hidden behind the fade.
 */
const SubNav = React.forwardRef<HTMLElement, SubNavProps>(
  ({ className, children, ...props }, ref) => {
    const navRef = React.useRef<HTMLElement>(null);
    React.useImperativeHandle(ref, () => navRef.current as HTMLElement);
    const { overflowing, atStart, atEnd } = useHorizontalOverflow(navRef);
    const fade = !overflowing
      ? undefined
      : !atStart && !atEnd
        ? 'both'
        : !atEnd
          ? 'end'
          : !atStart
            ? 'start'
            : undefined;

    React.useEffect(() => {
      const current = navRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
      current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }, []);

    return (
      <nav
        ref={navRef}
        data-slot="sub-nav"
        data-fade={fade}
        className={cn(
          '-mb-px overflow-x-auto overscroll-x-contain',
          'data-[fade=end]:[mask-image:linear-gradient(to_right,black_calc(100%_-_2rem),transparent)]',
          'rtl:data-[fade=end]:[mask-image:linear-gradient(to_left,black_calc(100%_-_2rem),transparent)]',
          'data-[fade=start]:[mask-image:linear-gradient(to_left,black_calc(100%_-_2rem),transparent)]',
          'rtl:data-[fade=start]:[mask-image:linear-gradient(to_right,black_calc(100%_-_2rem),transparent)]',
          'data-[fade=both]:[mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%_-_2rem),transparent)]',
          className,
        )}
        {...props}
      >
        <ul className="flex min-w-max items-stretch gap-1">{children}</ul>
      </nav>
    );
  },
);
SubNav.displayName = 'SubNav';

export interface SubNavItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean;
  /** Router link element to render as (e.g. `<Link to="/fleet/rollouts" />`). */
  render?: React.ReactElement;
  /** Optional count shown after the label in tabular mono. */
  count?: number | string;
}

const SubNavItem = React.forwardRef<HTMLAnchorElement, SubNavItemProps>(
  ({ active = false, render, count, className, children, ...props }, ref) => {
    const link = useRender<Record<string, unknown>, HTMLAnchorElement, true>({
      render: render ?? <a />,
      ref,
      props: {
        'aria-current': active ? 'page' : undefined,
        'data-active': active ? '' : undefined,
        className: cn(
          'inline-flex h-9 items-center gap-2 border-b-2 border-transparent px-2.5 text-sm font-medium text-[var(--color-foreground-3)] transition-colors',
          'hover:text-[var(--color-foreground)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus-ring)]',
          'data-[active]:border-[var(--color-foreground)] data-[active]:text-[var(--color-foreground)]',
          className,
        ),
        ...props,
        children: (
          <>
            {children}
            {count !== undefined ? (
              <span className="font-mono text-xs tabular-nums text-[var(--color-foreground-3)]">
                {count}
              </span>
            ) : null}
          </>
        ),
      },
      defaultTagName: 'a',
    });
    return <li className="flex">{link}</li>;
  },
);
SubNavItem.displayName = 'SubNavItem';

export interface SectionProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Section-level actions, right-aligned with the title. */
  actions?: React.ReactNode;
  /** A count or status rendered next to the title. */
  meta?: React.ReactNode;
  headingLevel?: 2 | 3;
  /** Drop the top rule (first section under a PageHeader). */
  flushTop?: boolean;
}

const Section = React.forwardRef<HTMLElement, SectionProps>(
  (
    {
      title,
      description,
      actions,
      meta,
      headingLevel = 2,
      flushTop = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const headingId = React.useId();
    const Heading = headingLevel === 2 ? 'h2' : 'h3';
    return (
      <section
        ref={ref}
        data-slot="section"
        aria-labelledby={headingId}
        className={cn(
          'flex flex-col gap-3 py-5',
          !flushTop && 'border-t border-[var(--color-hairline)]',
          className,
        )}
        {...props}
      >
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
          <div className="min-w-0 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <Heading
                id={headingId}
                className="text-sm font-semibold tracking-[-0.005em] text-[var(--color-foreground)]"
              >
                {title}
              </Heading>
              {meta}
            </div>
            {description ? (
              <p className="max-w-[72ch] text-pretty text-xs text-[var(--color-foreground-3)]">
                {description}
              </p>
            ) : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
        {children}
      </section>
    );
  },
);
Section.displayName = 'Section';

export { PageContainer, PageHeader, SubNav, SubNavItem, Section };
